import { ArticlePage, articleMeta, type ArticleData } from '@/site/article'

const article: ArticleData = {
  path: '/blog/skilky-dzvinkiv-vy-propuskayete',
  lead:
    'Більшість власників знають, скільки клієнтів прийшло. Скільки не дійшло, бо ніхто не взяв трубку, знають одиниці. Ось як порахувати це за тиждень з тим, що у вас уже є.',
  sections: [
    {
      h2: 'Навіщо це рахувати',
      blocks: [
        'Пропущений дзвінок не видно в жодному звіті про продажі. Клієнт, який не додзвонився, не скаржиться і не пише відгук: він просто телефонує в інше місце. Тому відчуття «ми майже нічого не пропускаємо» часто розходиться з реальністю, особливо ввечері, у вихідні і в години пік.',
      ],
    },
    {
      h2: 'Крок 1. Знайдіть, де видно пропущені',
      blocks: [
        'Залежно від того, як у вас влаштований телефон:',
        {
          list: [
            'Мобільний номер. Журнал викликів показує пропущені, але лише ті, що дійшли до телефону. Дзвінки, коли лінія була зайнята, там можуть не з’явитися. У багатьох операторів є послуга сповіщень про дзвінки, коли ви були недоступні чи зайняті: на час перевірки її варто увімкнути.',
            'Офісна чи віртуальна АТС. Майже всі мають журнал дзвінків зі статусом: прийнятий, пропущений, зайнято. Шукайте звіт зі статусом «не відповіли» чи «пропущені».',
            'Оператор зв’язку. В особистому кабінеті оператора часто є деталізація вхідних дзвінків, зокрема тих, що прийшли в неробочий час.',
          ],
        },
      ],
    },
    {
      h2: 'Крок 2. Зберіть тиждень даних',
      blocks: [
        'Одного дня мало: понеділок і субота поводяться зовсім по-різному. Візьміть звичайний тиждень без свят і для кожного дня випишіть:',
        {
          list: [
            'скільки всього було вхідних дзвінків;',
            'скільки з них пропущено або не прийнято через зайняту лінію;',
            'скільки пропущених припало на неробочий час;',
            'на скільки пропущених потім передзвонили і чим це закінчилось.',
          ],
        },
        'Окремо врахуйте повторні дзвінки з одного номера: людина, яка набрала тричі за п’ять хвилин, це один клієнт, а не три.',
      ],
    },
    {
      h2: 'Крок 3. Перетворіть дзвінки на гроші',
      blocks: [
        'Тепер проста арифметика:',
        {
          steps: [
            { title: 'Пропущені за місяць', text: 'Пропущені за тиждень, помножені приблизно на чотири.' },
            { title: 'Втрачені клієнти', text: 'Пропущені за місяць, помножені на частку дзвінків, які у вас зазвичай закінчуються записом чи замовленням. Цю частку ви знаєте краще за будь-яку статистику.' },
            { title: 'Втрачена виручка', text: 'Втрачені клієнти, помножені на середній чек. Якщо клієнти до вас повертаються, множте на те, що клієнт приносить за рік.' },
          ],
        },
        'Те саме можна порахувати повзунками в [калькуляторі на головній](/#problem).',
        'Частину пропущених ви все ж повертаєте передзвоном. Але передзвін через кілька годин часто запізнюється: клієнт міг уже домовитися з іншими. Тому рахуйте не «передзвонили», а «клієнт прийшов».',
      ],
    },
    {
      h2: 'Крок 4. Подивіться, коли саме ви пропускаєте',
      blocks: [
        'Розкладіть пропущені за годинами. Зазвичай вимальовується одна з картин:',
        {
          list: [
            'Пік уранці й в обід: адміністратор не встигає. Допоможе підстраховка, коли дзвінок, на який ніхто не відповів, приймає агент.',
            'Вечори й вихідні: потрібна відповідь у неробочий час. Тут підходить агент на години після закриття.',
            'Рівномірно впродовж дня: телефон просто не головна задача людини, яка його тримає. Варто віддати агенту всі перші дзвінки.',
          ],
        },
        'Для кожної з цих картин в Anytel є готовий [сценарій дзвінка](/funktsii/ivr-menyu), а як налаштувати роботу ввечері й у вихідні, описано на сторінці [«Робота 24/7»](/funktsii/robota-24-7).',
      ],
    },
    {
      h2: 'Як не рахувати вручну',
      blocks: [
        'Якщо дзвінки йдуть через Anytel, журнал у кабінеті показує кожен дзвінок: коли, скільки тривав, хто відповів і чим закінчився. На огляді є графік прийнятих і пропущених дзвінків по днях, а пропущені можна автоматично ставити в чергу передзвонів.',
      ],
    },
  ],
  faq: [
    {
      q: 'Скільки пропущених дзвінків це нормально?',
      a: 'Універсальної норми немає. Корисніше порівнювати себе з собою: скільки пропущено цього тижня і скільки після змін. Якщо пропущений хоч один дзвінок, який міг стати клієнтом, уже є що покращувати.',
    },
    {
      q: 'А якщо ми передзвонюємо на всі пропущені?',
      a: 'Це добра звичка. Але порахуйте, скільки таких передзвонів закінчуються записом. Різниця між «передзвонили» і «клієнт прийшов» і є вашими втратами.',
    },
  ],
  related: ['/blog/yak-ne-vtrachaty-dzvinky-pislya-18', '/blog/ai-agent-chy-administrator', '/funktsii/robota-24-7', '/funktsii/zapys-i-transkryptsiya-dzvinkiv'],
}

export const meta = articleMeta(article)

export default function Post() {
  return <ArticlePage article={article} />
}
